import BannerAdTracker from '@javascripts/advertisements/banner_ad_tracker';
import { PlayerAhoyEvent } from '../player_ahoy_event';
import isFeatureToggleOn from "@javascripts/utils/feature_toggle";

export default class PauseAdvertisement {
  static shouldInit({ data } = {}) {
    return !!data.pauseAdDomId()
  }

  constructor({ tech, data, playerDisplayAdState } = {}) {
    this._tech = tech;
    this._data = data;
    this._autoCloseOnEmbedTimeout = null;
    this._isGoogleTagDisplayCalled = false;
    this._adRequestDelay = 0;
    this.playerDisplayAdState = playerDisplayAdState
  }

  tech() {
    return this._tech;
  }

  data() {
    return this._data;
  }

  init() {
    this.appendAdContainer();
    this.bindTriggerPauseAd();
    this.bindCancelPauseAd();
    this.bindPlayControlClick();
    this.bindVideoClick();
    this.bindBigPauseButtonClick();
    this.bindEventPlay();
    this.bindIntervalEmbedCheck();
  }

  get name() {
    return 'pause'
  }

  appendAdContainer() {
    this._container = this.createAdContainer();
    this._dialogContent = this._container.querySelector('.pause-ad-dialog-content');
    this.tech().el().appendChild(this._container);
  }

  bindTriggerPauseAd() {
    this.tech().on('vp-start', () => {
      this.tech().on('trigger-pause-ad', () => {
        this.sendPauseUserTriggeredEvent();
        if (this.shouldTriggerPauseAd()) {
          this.playerDisplayAdState.tryShowAd(this)
        }
      });
    })
  }
  
  show(options) {
    this._adRequestDelay = window.setTimeout(this.requestAd.bind(this), this.data().pauseAdRequestDelay() * 1000);
  }

  requestAd() {
    if (this._isGoogleTagDisplayCalled) {
      window.googletag.cmd.push(this.refreshAd());
    } else {
      window.googletag.cmd.push(this.displayAd());
      this._isGoogleTagDisplayCalled = true;
    }
  }
  
  bindCancelPauseAd() {
    this.tech().on('vp-start', () => {
      this.tech().on('cancel-pause-ad', () => {
        window.clearTimeout(this._adRequestDelay);
      });
    });
  }
  
  shouldTriggerPauseAd() {
    return !!(
      !(this.data().embed() === 'true' &&
        this.tech().el().clientWidth() <= 300) &&
      (isFeatureToggleOn('enable_premier_user_banner_ads') ||
        !this.data().isPremier()) &&
      (window.googletag &&
        window.googletag.apiReady &&
        window.googletag.pubadsReady &&
        window.kmklabs &&
        window.kmklabs.gpt &&
        window.kmklabs.gpt.pause_ads)
    )
  }

  sendPauseUserTriggeredEvent() {
    this.tech().trigger({
      type: "vp-pause",
      userTriggered: true
    });
  }

  displayAd() {
    return function() {
      window.googletag.display(window.kmklabs.gpt.pause_ads);
    }
  }

  refreshAd() {
    return function() {
      window.googletag.pubads().refresh([window.kmklabs.gpt.pause_ads]);
    };
  }

  bindPlayControlClick() {
    document.querySelector('.vjs-play-control').addEventListener('click', () => {
      if (this.tech().paused()) {
        this.tech().trigger('trigger-pause-ad');
      }
    });
  }

  bindVideoClick() {
    this.tech().videoElement().addEventListener('click', () => {
      setTimeout(() => {
        if (this.tech().paused()) {
          this.tech().trigger('trigger-pause-ad');
        }
      });
    });
  }

  bindBigPauseButtonClick() {
    const videoContainer = this.tech().el().el();
    videoContainer.querySelector('.vjs-big-pause-button').addEventListener('click', () => {
      if (this.tech().paused()) {
        this.tech().trigger('trigger-pause-ad');
      }
    });

    videoContainer.querySelector('.vjs-big-pause-button').addEventListener('touchend', () => {
      if (this.tech().paused()) {
        this.tech().trigger('trigger-pause-ad');
      }
    });
  }

  bindEventPlay() {
    this.tech().on('play', () => {
      this.hideContainer();
      this._dialogContent.innerHTML = '';
      this.tech().trigger('cancel-pause-ad');
    });
  }

  bindIntervalEmbedCheck() {
    if (this.data().embed() == 'true') {
      this.startIntervalEmbedCheck();
    }
  }

  startIntervalEmbedCheck() {
    window.setInterval(() => {
      if (this.tech().el().clientWidth() <= 300) {
        this.tech().el().addClass('embed-floating-player');
      } else {
        this.tech().el().removeClass('embed-floating-player');
      }
    }, 100);
  }

  createAdContainer() {
    const container = document.createElement('div');
    container.id = 'pauseAdContainer';
    container.className = 'pause-ad-container hidden';
    container.onclick = () => {
      this.hideContainer();
      BannerAdTracker.sendEvent({ ad_event: 'CLOSE', uuid: this.data().playUUID() })
    };

    const dialog = document.createElement('div');
    dialog.className = 'pause-ad-dialog';
    dialog.onclick = (e) => {
      e.stopPropagation();
    };

    const closeButton = this.createAdCloseButton();
    const continueButton = this.createAdContinueButton();
    closeButton.onclick = () => {
      this.hideContainer();
      BannerAdTracker.sendEvent({ ad_event: 'CLOSE', uuid: this.data().playUUID() })
    };
    continueButton.onclick = () => {
      this.hideContainer();
      this.tech().play();
      BannerAdTracker.sendEvent({ ad_event: 'RESUME', uuid: this.data().playUUID() })
    };

    dialog.appendChild(this.createAdDialogContent());
    dialog.appendChild(continueButton);
    dialog.appendChild(closeButton);

    container.appendChild(dialog);

    return container;
  }

  createAdDialogContent() {
    this._adDialogContent = document.createElement('div');
    this._adDialogContent.className = 'pause-ad-dialog-content';
    this._adDialogContent.id = 'pauseAdDialogContent';
    if (this.data().pauseAdDomId() &&
      this.data().pauseAdDomId() != '') {
      this._adDialogContent.id = this.data().pauseAdDomId();
    }
    return this._adDialogContent;
  }

  createAdCloseButton() {
    const dom = document.createElement('button');
    dom.className = 'pause-ad-close-button';
    dom.innerHTML = '&times;';
    return dom;
  }

  createAdContinueButton() {
    const dom = document.createElement('button');
    dom.className = 'pause-ad-continue-button';
    dom.innerHTML = 'Resume Video';
    return dom;
  }

  showContainer() {
    this._container.classList.remove('hidden');
  }

  hideContainer() {
    this.playerDisplayAdState.tryHideAd(this);
  }

  hide() {
    window.clearTimeout(this._autoCloseOnEmbedTimeout);
    this._container.classList.add('hidden');
  }

  isReady() {
    return this.shouldTriggerPauseAd();
  }

  autoCloseOnEmbed() {
    if (this.data().embed() === 'true') {
      this._autoCloseOnEmbedTimeout = window.setTimeout(() => {
        this.hideContainer();
      }, 5000);
    }
  }

  tryShowContainer() {
    if (this.tech().hasStarted() && this.tech().paused()) {
      this.showContainer();
      this.autoCloseOnEmbed();
    }
  }
}
